import { ChangeDetectorRef, Component, OnInit, OnDestroy, ErrorHandler, ViewChild, ChangeDetectionStrategy, inject, signal } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { Router, ActivatedRoute } from '@angular/router'

import { MatCheckbox } from '@angular/material/checkbox'
import { MatSort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'

import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

import { Kirjanpitaja } from 'app/_jaettu-lemonator/model/kirjanpitaja'
import { KirjanpitajaService } from 'app/_angular/service/kirjanpitaja/kirjanpitaja.service'
import { KirjanpitajaComponentDataResolve } from 'app/_angular/_resolvers/kirjanpitaja.resolve'
import { KirjanpitajaLisaaUusiDialog } from './kirjanpitaja-lisaa-uusi.dialog'

@Component({
  templateUrl: './kirjanpitajat.component.html',
  styles: ['.mat-column-aktiivinen { max-width: 60px; text-align: center; justify-content:center; }'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class KirjanpitajatComponent implements OnInit, OnDestroy {
  @ViewChild(MatSort, { static: true }) _sort: MatSort
  @ViewChild(MatCheckbox) naytaKaikkiCheckbox: MatCheckbox

  naytettavatKolumnit: string[] = ['aktiivinen', 'etunimi', 'sukunimi', 'email']
  readonly kirjanpitajatLataa = signal(true)
  private readonly _changeDetectorRef = inject(ChangeDetectorRef)

  private ngUnsubscribe: Subject<void> = new Subject<void>()
  dataSource = new MatTableDataSource<Kirjanpitaja>([])

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private errorHandler: ErrorHandler,
    private kirjanpitajatService: KirjanpitajaService,
    private kirjanpitajaComponentDataResolve: KirjanpitajaComponentDataResolve,
    private dialog: MatDialog,
  ) {
    this.dataSource.sortData = (data: Kirjanpitaja[], sort: MatSort): Kirjanpitaja[] => {
      const active = sort.active
      const direction = sort.direction

      if (!active || direction === '') {
        return data
      }

      const directionMultiplier = direction === 'asc' ? 1 : -1

      const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })

      if (active === 'etunimi') {
        return data.sort((a, b) => {
          return collator.compare(a.etunimi, b.etunimi) * directionMultiplier
        })
      }

      if (active === 'sukunimi') {
        return data.sort((a, b) => {
          return collator.compare(a.sukunimi, b.sukunimi) * directionMultiplier
        })
      }

      if (active === 'aktiivinen') {
        return data.sort((a, b) => {
          return (a.poistettu === b.poistettu ? 0 : a.poistettu ? -1 : 1) * directionMultiplier
        })
      }

      if (active === 'email') {
        return data.sort((a, b) => {
          return collator.compare(a.email, b.email) * directionMultiplier
        })
      }

      return data
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  ngOnInit() {
    this._sort.sort({
      disableClear: false,
      id: 'sukunimi',
      start: 'asc',
    })
    this.dataSource.sort = this._sort

    this.kirjanpitajatService.kirjanpitajatObservable.pipe(takeUntil(this.ngUnsubscribe)).subscribe(
      kirjanpitajat => {
        this.dataSource.data = kirjanpitajat || []
        this.kirjanpitajatLataa.set(false)
        this._changeDetectorRef.markForCheck()
      },
      error => {
        this.errorHandler.handleError(error)
      },
    )
  }

  lisaa() {
    this.dialog
      .open<KirjanpitajaLisaaUusiDialog, void, string | undefined>(KirjanpitajaLisaaUusiDialog, { autoFocus: 'first-tabbable', width: '520px' })
      .afterClosed()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(kirjanpitajaAvain => {
        if (kirjanpitajaAvain) {
          this.router.navigate(['yllapito', 'kirjanpitajat', kirjanpitajaAvain])
        }
      })
  }

  muokkaa(kirjanpitaja: Kirjanpitaja) {
    this.kirjanpitajaComponentDataResolve.asetaOlemassaolevaData({ kirjanpitaja: kirjanpitaja })
    this.router.navigate(['yllapito', 'kirjanpitajat', kirjanpitaja.avain])
  }

  get kirjanpitajatDataSource(): MatTableDataSource<Kirjanpitaja> {
    return this.dataSource
  }
}
